/**
 * useDirectory — fetches the tenant user directory once and exposes a
 * stable `nameFor(id)` resolver for collaboration surfaces. Failures degrade
 * gracefully to short-id labels (the directory is presentational, never a gate).
 */

import { useEffect, useMemo, useState } from 'react';
import { type DirectoryUser, displayName, listDirectoryUsers } from '@/api/directory';

export interface Directory {
  users: Map<string, DirectoryUser>;
  nameFor: (id: string | null | undefined) => string;
  loading: boolean;
}

export function useDirectory(): Directory {
  const [users, setUsers] = useState<Map<string, DirectoryUser>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    listDirectoryUsers()
      .then((list) => {
        if (cancelled) return;
        setUsers(new Map(list.map((u) => [u.id, u])));
      })
      .catch(() => {
        /* directory is presentational; ignore load errors */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(
    () => ({ users, nameFor: (id) => displayName(users, id), loading }),
    [users, loading],
  );
}
